// src/sync/syncOrchestrator.js
import { authSyncAPI } from "./authSyncAPI";
import { throttle } from "../../utils/throttle";
import { startHydration, endHydration } from "../../utils/gamePersistence";

/** Keys we must NEVER upload or restore back */
const EXCLUDE_KEYS = new Set([
  // auth/session
  "authToken",
  "refreshToken",
  "syncToken",
  // runtime & telemetry
  "lastLocalChange",
  "lastServerUpdatedAt",
  "lastSyncTime",
  "userCredentials",
  "deviceId",
  "EXIT_RESUME_TOKEN",
]);

/** OPTIONAL: only upload keys that match a namespace/prefix */
const ONLY_PREFIX = null; // e.g. "WTUFO_" to whitelist keys; keep null to include all

/** Build a snapshot of (almost) entire localStorage */
export function buildSnapshotFromLocalStorage() {
  const data = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;

      // filter by prefix (if set)
      if (ONLY_PREFIX && !key.startsWith(ONLY_PREFIX)) continue;

      // exclude sensitive/ephemeral keys
      if (EXCLUDE_KEYS.has(key)) continue;

      const raw = localStorage.getItem(key);
      if (raw == null) continue;
      try {
        data[key] = JSON.parse(raw);
      } catch {
        data[key] = raw; // allow non-JSON strings
      }
    }
  } catch {
    // best-effort
  }
  return data;
}

/** Restore snapshot into localStorage (skips excluded keys) */
export function restoreSnapshotToLocalStorage(snapshotData = {}) {
  // CRITICAL FIX: Start hydration to prevent dirty marking
  startHydration();

  try {
    Object.entries(snapshotData).forEach(([k, v]) => {
      try {
        if (EXCLUDE_KEYS.has(k)) return;
        if (ONLY_PREFIX && !k.startsWith(ONLY_PREFIX)) return;
        const payload = typeof v === "string" ? v : JSON.stringify(v);
        localStorage.setItem(k, payload);
      } catch {
        // best-effort
      }
    });
    localStorage.setItem("lastServerUpdatedAt", new Date().toISOString());
  } finally {
    // CRITICAL FIX: End hydration and notify Redux store
    endHydration();
  }
}

/** Simple event helpers */
const emit = (name, detail) =>
  window.dispatchEvent(new CustomEvent(name, { detail }));

class SyncOrchestrator {
  interval = null;
  syncing = false;
  acked = false;

  // dirty flag + throttled force sync
  dirty = false;
  throttledForce = throttle(
    (reason = "manual") => this.syncOnce({ force: true, reason }),
    3000
  );

  // ACK guard
  ackInFlight = false;
  lastAckAt = 0;
  ackBackoffMs = 5000;

  constructor() {
    window.addEventListener("WTUFO_DIRTY", () => this.markDirty());
  }

  markDirty() {
    this.dirty = true;
  }

  forceSyncNow(reason = "manual") {
    this.throttledForce(reason);
  }

  handleUnauthorized() {
    // Stop everything and let Axios interceptor handle redirect to /login
    this.stop();
    // optional: surface a session event for UI (spinner, banners, etc.)
    window.dispatchEvent(
      new CustomEvent("session:error", { detail: "UNAUTHORIZED" })
    );
  }

  async safeAck() {
    const now = Date.now();
    if (this.ackInFlight) return;
    if (now - this.lastAckAt < this.ackBackoffMs) return;

    this.ackInFlight = true;
    try {
      await authSyncAPI.syncAck();
      this.acked = true;
      this.lastAckAt = Date.now();
    } finally {
      this.ackInFlight = false;
    }
  }

  start = async () => {
    // 1) Try to resume (best effort)
    try {
      const { gameSnapshot } = await authSyncAPI.resume();
      if (gameSnapshot?.data) {
        const localTime = localStorage.getItem("lastLocalChange");
        const serverTime = gameSnapshot.updatedAt;
        if (
          !localTime ||
          (serverTime && new Date(serverTime) > new Date(localTime))
        ) {
          // FIXED: Now properly uses hydration guards
          restoreSnapshotToLocalStorage(gameSnapshot.data);
        }
      }
    } catch (error) {
      // If resume itself is unauthorized, bail out immediately
      if (error?.response?.status === 401) {
        this.handleUnauthorized();
        return;
      }
      // else ignore (offline etc.)
    }

    // 2) ACK once (but stop on 401)
    try {
      await this.safeAck();
    } catch (error) {
      this.acked = false;
      if (error?.response?.status === 401) {
        this.handleUnauthorized();
        return;
      }
    }

    // FIXED: Mark as dirty initially to ensure first sync happens
    this.dirty = true;

    // 3) Start interval
    if (!this.interval) {
      this.interval = setInterval(
        () => this.syncOnce({ reason: "interval" }),
        60000
      );
    }
  };

  stop = () => {
    if (this.interval) clearInterval(this.interval);
    this.interval = null;
    this.syncing = false;
    this.acked = false;
  };

  syncOnce = async ({ force = false, reason = "interval" } = {}) => {
    if (this.syncing) return;

    // Skip if nothing changed locally since last success (unless forced)
    if (!force && !this.dirty) return;

    this.syncing = true;
    emit("sync:start", { reason, force });

    try {
      if (!this.acked) {
        await this.safeAck();
      }

      // Upload full localStorage snapshot (minus excluded keys)
      const snapshot = buildSnapshotFromLocalStorage();
      await authSyncAPI.syncSnapshot(snapshot);

      localStorage.setItem("lastSyncTime", new Date().toISOString());
      this.dirty = false;
      emit("sync:success", { reason, force });
    } catch (error) {
      emit("sync:error", { reason, force, error });

      const status = error?.response?.status;
      const code = error?.response?.data?.errorCode;

      // 401 → stop orchestrator; Axios interceptor will redirect to /login
      if (status === 401) {
        this.handleUnauthorized();
        return;
      }

      if (code === "SYNC_NOT_ACKED" || code === "ACK_TIMEOUT") {
        // Try a one-time re-ACK (guarded/backoff)
        try {
          await this.safeAck();
        } catch (ackErr) {
          // If this also became 401, stop
          if (ackErr?.response?.status === 401) {
            this.handleUnauthorized();
            return;
          }
        }
      } else if (code === "STALE_SNAPSHOT") {
        try {
          const { gameSnapshot } = await authSyncAPI.resume();
          if (gameSnapshot?.data) {
            // FIXED: Properly use hydration guards here too
            restoreSnapshotToLocalStorage(gameSnapshot.data);
          }
        } catch (resumeErr) {
          if (resumeErr?.response?.status === 401) {
            this.handleUnauthorized();
            return;
          }
        }
      } else if (
        code === "INVALID_SESSION" ||
        code === "SESSION_EXPIRED" ||
        code === "DEVICE_MISMATCH"
      ) {
        // Treat other terminal session errors the same
        this.handleUnauthorized();
        return;
      }
    } finally {
      this.syncing = false;
    }
  };
}

export const syncOrchestrator = new SyncOrchestrator();
